"use client";

import React from "react";
import {
  CounterMuted,
  CounterRow,
  IndicatorRow,
  LineDot,
  PillDot,
  ProgressFill,
  ProgressTrack,
  ProgressTrackBg,
} from "../DynamicHighlights.styles";
import type { HighlightsIndicatorType } from "../types";
import { ARIA_GO_TO, ARIA_INDICATOR } from "../strings";

/**
 * O indicador do carrossel, nos quatro formatos — compartilhado pelo trilho e
 * pelo rodapé do modal, para que a escolha do Champion valha nos dois lugares.
 *
 * Os tipos que desenham uma peça por item (`pill`, `lines`) são CLICÁVEIS; a
 * barra e o contador não, porque não há alvo por item.
 */
export type CarouselIndicatorProps = {
  type: HighlightsIndicatorType;
  /** Quantos passos existem. */
  count: number;
  /** Passo atual, base zero. */
  index: number;
  color: string;
  align?: "start" | "center";
  /** Vai para o passo `n`. Ausente = indicador só informativo. */
  onGoTo?: (index: number) => void;
};

/**
 * Quantas peças a JANELA mostra quando há itens demais.
 *
 * Uma coleção sem limite pode ter centenas de destaques, e peça por item não
 * escala: ~200 não cabem na coluna (só ~68 círculos ou ~30 traços), e o flex as
 * espremeria até virarem uma faixa cinza ilegível. Em vez de degradar o formato
 * escolhido, mostramos uma janela que desliza com a posição — o padrão do
 * Instagram Stories.
 *
 * Ímpar de propósito: com o ativo no centro sobra o mesmo número de peças dos
 * dois lados.
 */
const INDICATOR_WINDOW = 7;

/**
 * Faixa de itens visível: a janela desliza com o índice e trava nas pontas,
 * para nunca mostrar posição que não existe.
 */
export const indicatorWindow = ({
  count,
  index,
  size = INDICATOR_WINDOW,
}: {
  count: number;
  index: number;
  size?: number;
}): { start: number; end: number } => {
  if (count <= size) return { start: 0, end: count };

  const half = Math.floor(size / 2);
  const start = Math.max(0, Math.min(index - half, count - size));

  return { start, end: start + size };
};

/** Os que desenham uma peça por item. A barra e o contador são peça única. */
const DOT_COMPONENT = {
  pill: PillDot,
  lines: LineDot,
} as const;

type DotType = keyof typeof DOT_COMPONENT;

const isDotType = (type: HighlightsIndicatorType): type is DotType =>
  type === "pill" || type === "lines";

export const CarouselIndicator = ({
  type,
  count,
  index,
  color,
  align = "start",
  onGoTo,
}: CarouselIndicatorProps) => {
  if (count <= 0) return null;

  // `bar` é a BARRA DE PROGRESSO — é o que o `IndicatorTypeTabs` desenha nessa opção.
  if (type === "bar") {
    const progressPct = ((index + 1) / count) * 100;

    return (
      <IndicatorRow role="group" aria-label={ARIA_INDICATOR} $align={align}>
        <ProgressTrack>
          <ProgressTrackBg $color={color} />
          <ProgressFill $color={color} style={{ width: `${progressPct}%` }} />
        </ProgressTrack>
      </IndicatorRow>
    );
  }

  if (type === "counter") {
    return (
      <IndicatorRow role="group" aria-label={ARIA_INDICATOR} $align={align}>
        <CounterRow $color={color}>
          <span>{index + 1}</span>
          <CounterMuted>/</CounterMuted>
          <CounterMuted>{count}</CounterMuted>
        </CounterRow>
      </IndicatorRow>
    );
  }

  const DotComponent = isDotType(type) ? DOT_COMPONENT[type] : PillDot;
  const { start, end } = indicatorWindow({ count, index });

  return (
    <IndicatorRow
      role="group"
      aria-label={ARIA_INDICATOR}
      $align={align}
    >
      {Array.from({ length: end - start }, (_, offset) => start + offset).map((item) => (
        <DotComponent
          key={item}
          type="button"
          aria-label={ARIA_GO_TO(item)}
          aria-current={item === index}
          $active={item === index}
          /*
           * Peça da BORDA da janela encolhe — é o que sinaliza "há mais itens
           * deste lado" sem precisar de reticências.
           */
          $edge={
            (start > 0 && item === start) || (end < count && item === end - 1)
          }
          $color={color}
          onClick={onGoTo ? () => onGoTo(item) : undefined}
        />
      ))}
    </IndicatorRow>
  );
};
